/**
 * ThemeContext - Global theme state management
 * Supports 4 themes: light, dark, cyberpunk, luxury
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const THEMES = {
    light: {
        id: 'light',
        name: 'Light',
        icon: '☀️',
        colors: {
            background: '#ffffff',
            backgroundSecondary: '#f8fafc',
            text: '#1e293b',
            textSecondary: '#64748b',
            primary: '#3b82f6',
            primaryHover: '#2563eb',
            accent: '#6366f1',
            border: '#e2e8f0',
            card: '#ffffff',
            cardHover: '#f1f5f9',
            sidebar: 'linear-gradient(to bottom, #0f172a, #1e293b, #334155)',
            sidebarText: '#ffffff',
            success: '#10b981',
            warning: '#f59e0b',
            error: '#ef4444',
        }
    },
    dark: {
        id: 'dark',
        name: 'Dark',
        icon: '🌙',
        colors: {
            background: '#111827',
            backgroundSecondary: '#1f2937',
            text: '#f9fafb',
            textSecondary: '#9ca3af',
            primary: '#8b5cf6',
            primaryHover: '#a78bfa',
            accent: '#c4b5fd',
            border: '#374151',
            card: '#1f2937',
            cardHover: '#374151',
            sidebar: 'linear-gradient(to bottom, #0f172a, #1e293b, #334155)',
            sidebarText: '#f9fafb',
            success: '#34d399',
            warning: '#fbbf24',
            error: '#f87171',
        }
    },
    cyberpunk: {
        id: 'cyberpunk',
        name: 'Cyberpunk',
        icon: '🤖',
        colors: {
            background: '#0f172a',
            backgroundSecondary: '#1e293b',
            text: '#22c55e',
            textSecondary: '#94a3b8',
            primary: '#22c55e',
            primaryHover: '#4ade80',
            accent: '#10b981',
            border: 'rgba(34, 197, 94, 0.3)',
            card: '#1e293b',
            cardHover: '#334155',
            sidebar: 'linear-gradient(to bottom, #0f172a, #1e293b, #334155)',
            sidebarText: '#22c55e',
            success: '#22c55e',
            warning: '#eab308',
            error: '#ef4444',
        }
    },
    luxury: {
        id: 'luxury',
        name: 'Luxury',
        icon: '👑',
        colors: {
            background: '#1a1a1a',
            backgroundSecondary: '#2d2d2d',
            text: '#d4af37',
            textSecondary: '#c0c0c0',
            primary: '#d4af37',
            primaryHover: '#f4cf47',
            accent: '#c0a000',
            border: '#d4af3733',
            card: '#2d2d2d',
            cardHover: '#3d3d3d',
            sidebar: 'linear-gradient(to bottom, #0f172a, #1e293b, #334155)',
            sidebarText: '#d4af37',
            success: '#50c878',
            warning: '#ffd700',
            error: '#dc143c',
        }
    }
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Load from localStorage or default to light
        const saved = localStorage.getItem('cikgu-theme');
        return saved && THEMES[saved] ? saved : 'light';
    });

    const [forcedTheme, setForcedTheme] = useState(null);

    // Save theme preference to localStorage
    useEffect(() => {
        localStorage.setItem('cikgu-theme', theme);
    }, [theme]);

    // Apply theme to document (preference or forced)
    useEffect(() => {
        const activeTheme = forcedTheme || theme;
        const themeData = THEMES[activeTheme];
        if (!themeData) return;

        const root = document.documentElement;
        const colors = themeData.colors;

        // Set CSS custom properties
        root.style.setProperty('--bg-primary', colors.background);
        root.style.setProperty('--bg-secondary', colors.backgroundSecondary);
        root.style.setProperty('--text-primary', colors.text);
        root.style.setProperty('--text-secondary', colors.textSecondary);
        root.style.setProperty('--color-primary', colors.primary);
        root.style.setProperty('--color-primary-hover', colors.primaryHover);
        root.style.setProperty('--color-accent', colors.accent);
        root.style.setProperty('--border-color', colors.border);
        root.style.setProperty('--card-bg', colors.card);
        root.style.setProperty('--card-hover', colors.cardHover);
        root.style.setProperty('--sidebar-bg', colors.sidebar);
        root.style.setProperty('--sidebar-text', colors.sidebarText);
        root.style.setProperty('--success-color', colors.success);
        root.style.setProperty('--warning-color', colors.warning);
        root.style.setProperty('--error-color', colors.error);

        // Set data attribute for CSS selectors
        root.setAttribute('data-theme', activeTheme);
    }, [theme, forcedTheme]);

    const changeTheme = (newTheme) => {
        if (THEMES[newTheme]) {
            setTheme(newTheme);
        }
    };

    return (
        <ThemeContext.Provider value={{
            theme,
            setTheme: changeTheme,
            setForcedTheme,
            themeData: THEMES[forcedTheme || theme],
            themes: THEMES
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
